import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  InputAdornment,
  Chip,
} from '@mui/material';
import { PhotoCapture } from '@/components/inspection/PhotoCapture';
import type { ChecklistTemplateItem, BookingChecklistEntry, ChecklistPhoto } from '@/hooks/useChecklists';

interface Props {
  item: ChecklistTemplateItem;
  entry?: BookingChecklistEntry;
  onChange: (entry: Partial<BookingChecklistEntry> & { itemId: string }) => void;
  disabled?: boolean;
  photos?: ChecklistPhoto[];
  onAddPhoto?: (itemId: string, file: File) => Promise<void>;
  onDeletePhoto?: (itemId: string, photoId: string) => Promise<void>;
  isUploadingPhoto?: boolean;
}

export function ChecklistItemRenderer({ item, entry, onChange, disabled, photos, onAddPhoto, onDeletePhoto, isUploadingPhoto }: Props) {
  const { t } = useTranslation('checklists');

  const isChecked = entry?.checked ?? false;

  const handleCheckChange = (checked: boolean) => {
    onChange({ itemId: item.id, checked });
  };

  const handleValueChange = (value: string | number) => {
    onChange({ itemId: item.id, checked: true, value });
  };

  const handleNotesChange = (notes: string) => {
    onChange({ itemId: item.id, checked: isChecked, notes });
  };

  return (
    <Box
      sx={{
        py: 1.5,
        px: 2,
        borderRadius: 1,
        bgcolor: isChecked ? 'success.50' : 'transparent',
        border: '1px solid',
        borderColor: isChecked ? 'success.200' : 'divider',
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1}>
        {item.type === 'checkbox' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleCheckChange(e.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={isChecked ? 400 : 500}>
                  {item.label}
                </Typography>
                {item.required && !isChecked && (
                  <Chip label={t('booking.requiredItem')} size="small" color="warning" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                )}
              </Box>
            }
            sx={{ flex: 1, m: 0 }}
          />
        )}

        {item.type === 'text' && (
          <Box flex={1}>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              {item.label}
              {item.required && <Typography component="span" color="error.main"> *</Typography>}
            </Typography>
            <TextField
              value={entry?.value ?? ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder={item.config?.placeholder}
              size="small"
              fullWidth
              multiline
              maxRows={3}
              disabled={disabled}
            />
          </Box>
        )}

        {item.type === 'numeric' && (
          <Box flex={1}>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              {item.label}
              {item.required && <Typography component="span" color="error.main"> *</Typography>}
            </Typography>
            <TextField
              value={entry?.value ?? ''}
              onChange={(e) => handleValueChange(e.target.value ? Number(e.target.value) : '')}
              type="number"
              size="small"
              sx={{ maxWidth: 200 }}
              disabled={disabled}
              slotProps={{
                input: item.config?.unit ? {
                  endAdornment: <InputAdornment position="end">{item.config.unit}</InputAdornment>,
                } : undefined,
                htmlInput: {
                  min: item.config?.min,
                  max: item.config?.max,
                },
              }}
            />
          </Box>
        )}

        {item.type === 'photo' && (
          <Box flex={1}>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              {item.label}
              {item.required && <Typography component="span" color="error.main"> *</Typography>}
              {item.photoConfig?.requirePhoto && (
                <Chip label={t('booking.photoRequired')} size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
              )}
            </Typography>
            {onAddPhoto && (
              <PhotoCapture
                photos={(photos ?? []).map((p) => ({
                  id: p.id,
                  url: p.fileUrl,
                  thumbnailUrl: p.thumbnailUrl ?? undefined,
                  caption: p.caption ?? undefined,
                }))}
                onCapture={!disabled ? (file) => onAddPhoto(item.id, file) : undefined}
                onDelete={!disabled && onDeletePhoto ? (photoId) => onDeletePhoto(item.id, photoId) : undefined}
                disabled={disabled || isUploadingPhoto}
                maxPhotos={item.photoConfig?.maxPhotos ?? 5}
                itemName={item.label}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Optional notes for any item type */}
      {item.type !== 'text' && isChecked && (
        <TextField
          value={entry?.notes ?? ''}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="..."
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          disabled={disabled}
        />
      )}

      {/* Photo attachment for all non-photo items */}
      {item.type !== 'photo' && onAddPhoto && (
        <Box sx={{ mt: 1 }}>
          {item.photoConfig?.requirePhoto && (photos ?? []).length === 0 && (
            <Chip label={t('booking.photoRequired')} size="small" color="warning" variant="outlined" sx={{ mb: 0.5, height: 20, fontSize: '0.7rem' }} />
          )}
          <PhotoCapture
            photos={(photos ?? []).map((p) => ({
              id: p.id,
              url: p.fileUrl,
              thumbnailUrl: p.thumbnailUrl ?? undefined,
              caption: p.caption ?? undefined,
            }))}
            onCapture={!disabled ? (file) => onAddPhoto(item.id, file) : undefined}
            onDelete={!disabled && onDeletePhoto ? (photoId) => onDeletePhoto(item.id, photoId) : undefined}
            disabled={disabled || isUploadingPhoto}
            maxPhotos={item.photoConfig?.maxPhotos ?? 5}
            itemName={item.label}
          />
        </Box>
      )}
    </Box>
  );
}
